<template>
  <div>
    <div class="flex justify-between items-start gap-lg">
      <h1>Party Role Selector</h1>

      <BaseButton
        class="!p-0 ml-auto"
        icon="arrow-down"
        variant="invisible"
        @click.native="showParties = !showParties"
      >
        show json
      </BaseButton>
    </div>

    <div
      v-for="(example, index) in examples"
      :key="index"
      class="p-lg mb-xxl bg-whiteOverride rounded-sm"
    >
      <h2>
        {{ example.documents.length }} Documents
        {{ example.parties.length }} Parties
      </h2>

      <HeaderContainer
        v-for="partyNumber in example.parties.length"
        :key="partyNumber"
        class="border-offWhite mb-sm last:mb-0"
      >
        <template v-slot:header>
          <p class="py-sm m-0 font-bold text-disabledGrey">
            Example Party {{ partyNumber }}
          </p>
        </template>

        <PartyRoleSelector
          :roles="example.roles"
          :partyIndex="partyNumber - 1"
          @toggle="example.toggleRoleSelected"
        />
      </HeaderContainer>

      <pre
        v-if="showParties"
        v-for="(party, index) in example.parties"
        :key="index"
        >{{ JSON.stringify(party, null, 2) }}
      </pre>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { ref } from 'vue';
import { faker } from '@faker-js/faker';

import { useAssignRoles, useRoles } from '@/composables/roles/roles';
import {
  createDocumentRoles,
  createDocuments,
} from '@/data/factories/documents';
import { createNewParties } from '@/store/modules/envelopes/factories/parties';

import { HeaderContainer } from '@/components//library/containers/HeaderContainer';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import { PartyRoleSelector } from '@/components/library/partyRoleSelectors/PartyRoleSelector';

/** @typedef {import('@/data/schema/parties').TNewOrSavedParties} TUpdateParties */
/** @typedef {import('@/data/schema/documents').TDocuments} TDocuments */

/**
 * @param {TUpdateParties} parties
 * @param {TDocuments} documents
 */
function createExample(parties, documents) {
  const partiesRef = ref(parties);
  const documentsRef = ref(documents);

  return {
    parties,
    documents,
    roles: useRoles(partiesRef, documentsRef),
    ...useAssignRoles(partiesRef, documentsRef),
  };
}

const roles = createDocumentRoles(12);
const examples = [
  createExample(
    createNewParties(1, [{ role_ids: [] }]),
    createDocuments(1, [{ parties: [roles[0], roles[1]] }]),
  ),
  createExample(
    createNewParties(1, [{ role_ids: [roles[0].id] }]),
    createDocuments(1, [{ parties: [roles[0], roles[1]] }]),
  ),
  createExample(
    createNewParties(1, [{ role_ids: [] }]),
    createDocuments(2, [
      { parties: [roles[2]] },
      { title: 'Template without roles', parties: [] },
    ]),
  ),
  createExample(
    createNewParties(4, [
      { role_ids: [roles[3].id] },
      { role_ids: [roles[4].id] },
      { role_ids: [] },
      {
        role_ids: [],
        role: 'copy',
      },
    ]),
    createDocuments(3, [
      { title: faker.lorem.sentence(), parties: [roles[3]] },
      { parties: [roles[4]] },
      { parties: [roles[5]] },
    ]),
  ),
  createExample(
    createNewParties(2, [
      { role_ids: [roles[6].id] },
      { role_ids: [roles[7].id] },
    ]),
    createDocuments(2, [
      { parties: [roles[6], roles[7]] },
      { parties: [roles[8]] },
    ]),
  ),
  createExample(
    createNewParties(3, [
      { role_ids: [roles[9].id] },
      { role_ids: [roles[10].id, roles[11].id] },
      { role_ids: [] },
    ]),
    createDocuments(2, [
      { parties: [roles[9], roles[10]] },
      { parties: [roles[11]] },
    ]),
  ),
];

const showParties = ref(false);
</script>
